import React, { useEffect, Suspense } from "react";

import "./App.scss";
import { SnackbarProvider } from "notistack";
import { AuthProvider } from "oidc-react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { useNavigate, Outlet } from "react-router-dom";

import {
  Storage,
  Menu as MenuIcon,
  AccountCircle,
  Add,
  Sell,
  Close as CloseIcon,
  BugReport as BugIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Tooltip,
  Typography,
  Button,
  Stack,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";

import oidcConfiguration from "./auth/oidcConfig";
import Footer from "./components/footer";
import Loader from "./components/loader";
import AuthGuard, { clearPrevious, previous } from "./components/protected";
import { testMode } from "./config";
import usePlausible from "./hooks/usePlausible";
import Logo from "./logo";
import darkTheme from "./theme";
import {
  Cluster,
  CreateCluster,
  Home,
  LinkAccount,
  Pricing,
  PrivacyPolicy,
  Profile,
  SignupForm,
  SingleCluster,
  TOS,
} from "./pages";

interface Nav {
  path: string;
  title: string;
  element: React.ReactElement;
  icon: React.ReactElement;
  children?: Nav[];
}

const children = [
  {
    path: "/",
    element: <Home />,
    title: "Home",
  },
  {
    path: "/cluster/:cluster",
    element: (
      <AuthGuard>
        <SingleCluster />
      </AuthGuard>
    ),
  },
  {
    path: "/signup",
    element: (
      <AuthGuard>
        <SignupForm />
      </AuthGuard>
    ),
  },
  {
    path: "/privacy",
    element: <PrivacyPolicy />,
  },
  {
    path: "/tos",
    element: <TOS />,
  },
  {
    path: "/link",
    element: (
      <AuthGuard>
        <LinkAccount />
      </AuthGuard>
    ),
    //icon: <Link />,
    title: "Cluster",
  },
  {
    path: "/pricing",
    element: <Pricing />,
    icon: <Sell />,
    title: "Pricing",
  },
  {
    path: "/clusters",
    element: (
      <AuthGuard>
        <Cluster />
      </AuthGuard>
    ),
    icon: <Storage />,
    title: "Cluster List",
    // children: [],
  },
  {
    path: "/cluster/new",
    title: "New Cluster",
    icon: <Add />,
    element: (
      <AuthGuard>
        <CreateCluster />
      </AuthGuard>
    ),
  },
  {
    path: "/profile",
    element: (
      <AuthGuard>
        <Profile />
      </AuthGuard>
    ),
    icon: <AccountCircle />,
    title: "Profile",
  },
];

// Filter out Error Test page from navigation if not in test mode
const filteredChildren = children
  .filter((c) => {
    if (c.path === "/error-test" && !testMode) {
      return false;
    }
    return c.title;
  })
  .map((c) => c as Nav);

export const nav = [
  {
    path: "/",
    element: <Layout navs={filteredChildren} />, // Haxx
    children,
  },
];

export function Layout({ navs }: { navs: Nav[] }) {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const isMobile = useMediaQuery("(max-width:900px)");
  const plausible = usePlausible();

  // TODO
  useEffect(() => {
    if (previous) {
      const p = previous;
      setTimeout(() => {
        clearPrevious();
        navigate(p);
      }, 1);
    }
  }, [previous]);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const navItems = navs.filter((n) => !!n.icon);

  const renderMobileDrawer = () => (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={toggleDrawer}
      PaperProps={{
        sx: {
          width: 250,
          backgroundColor: (theme) => theme.palette.background.default,
        },
      }}
    >
      <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
        <IconButton onClick={toggleDrawer}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {navItems.map((n) => (
          <ListItem
            button
            key={n.path}
            onClick={() => {
              // Track mobile navigation event
              plausible.trackEvent("mobile_navigation_click", {
                section: n.title,
                path: n.path,
              });
              navigate(n.path);
              toggleDrawer();
            }}
          >
            <ListItemIcon>{n.icon}</ListItemIcon>
            <ListItemText primary={n.title} />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={1}
        sx={{
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(26, 30, 35, 0.95)",
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              "&:hover": { cursor: "pointer" },
            }}
            onClick={() => {
              plausible.trackEvent("logo_click", { destination: "home" });
              navigate("/");
            }}
          >
            <Box sx={{ width: 40, height: 40, mr: 2 }}>
              <Logo />
            </Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Microcumulus
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {!isMobile ? (
            <Stack direction="row" spacing={1}>
              {navItems.map((n) => (
                <Tooltip key={n.path} title={n.title}>
                  <Button
                    variant="text"
                    color="inherit"
                    startIcon={n.icon}
                    onClick={() => {
                      // Track navigation event
                      plausible.trackEvent("navigation_click", {
                        section: n.title,
                        path: n.path,
                      });
                      navigate(n.path);
                    }}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      "&:hover": {
                        backgroundColor: "rgba(67, 211, 255, 0.1)",
                      },
                    }}
                  >
                    {n.title}
                  </Button>
                </Tooltip>
              ))}
            </Stack>
          ) : (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {isMobile && renderMobileDrawer()}

      <Box 
        component="main" 
        sx={{ 
          p: 3,
          minHeight: "calc(100vh - 64px - 73px)", // Account for AppBar height and Footer height
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Outlet />
      </Box>
      
      <Footer />
    </>
  );
}

const router = createBrowserRouter(nav);

function App() {
  return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <StyledEngineProvider injectFirst>
          <AuthProvider {...oidcConfiguration}>
            <SnackbarProvider
              maxSnack={3}
              autoHideDuration={5000}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <RouterProvider router={router} />
            </SnackbarProvider>
          </AuthProvider>
        </StyledEngineProvider>
      </ThemeProvider>
  );
}

export default App;